import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";

const DAD_AND_ME = "https://customer-assets.emergentagent.com/job_grief-admin-guide/artifacts/69qymz1c_Dad%20and%20me.jpg";

export default function About() {
  return (
    <div className="max-w-3xl mx-auto px-6 md:px-12 py-16 md:py-24" data-testid="about-page">
      <div className="text-xs uppercase tracking-[0.2em] text-[#6B7F62] mb-4">A note from Lauren</div>
      <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl text-[#2D332D] leading-[1.05] tracking-tight mb-10">
        About At The End of The Day
      </h1>

      <div className="space-y-6 text-[#3f463f] leading-relaxed text-lg">
        <p>
          When my dear Dad died in 2023, even though he'd been poorly for a while, it all happened quite suddenly. We hadn't really talked about the practical side of things &mdash; his wishes, his paperwork, where anything important was kept, or what he wanted us to know. On top of the obvious sadness, we found ourselves sifting through random piles of papers, trying to work out where to start and what needed doing first. That extra layer of stress, on top of the grief, really stayed with me.
        </p>

        <p>
          As the admin organiser at home, the thought of my family having to go through something similar, with no idea where to start, made me feel incredibly anxious. So fairly quickly, I found the time to write everything out for them. I did it in stages &mdash; the names of all my different accounts, our home admin, my wishes, important contacts, and letters for each of them &mdash; so that if the unthinkable ever happened to me, they wouldn't have that extra burden to deal with on top of loss.
        </p>

        <p>
          That's where the idea for <span className="font-medium text-[#2D332D]">At The End of The Day</span> was born.
        </p>

        <p>It's two-fold:</p>
        <ul className="list-disc pl-6 space-y-2 marker:text-[#6B7F62]">
          <li>A gentle, practical guide to navigating bereavement.</li>
          <li>A step-by-step planning system to help you get organised now, so that one day your loved ones have a clearer path and a little less stress in what will already be a difficult time.</li>
        </ul>

        <p>
          The most important thing for me is getting the message out that the kindest thing we can leave behind for our loved ones isn't just memories &mdash; it's a clear plan. Planning for this isn't only for when we're old, because we never really know what's around the corner.
        </p>

        <p>
          I hope it would make you feel reassured for your own peace of mind, knowing it's one less thing for them to have to deal with if you're no longer here. And if this site helps even just one of you and your family feel a little less overwhelmed than we did, that will be a real comfort to me.
        </p>

        <p className="font-serif text-2xl text-[#2D332D] pt-3 leading-tight">
          Lauren Jordan
        </p>
      </div>

      {/* Photo */}
      <figure className="mt-14 md:mt-16">
        <div className="rounded-2xl overflow-hidden bg-[#F3F1ED] border border-[#EAE6E1] shadow-sm">
          <img
            src={DAD_AND_ME}
            alt="Lauren dancing with her Dad"
            data-testid="about-photo"
            loading="lazy"
            className="w-full h-auto block"
          />
        </div>
        <figcaption className="mt-3 text-sm text-[#8A9488] text-center italic">
          My Dad and I.
        </figcaption>
      </figure>

      {/* Gentle CTA */}
      <div className="mt-16 rounded-2xl bg-[#E9ECE8] border border-[#9EAD94]/40 p-6 md:p-8">
        <div className="font-serif text-2xl text-[#2D332D] mb-2">Where you can start</div>
        <p className="text-[#576056] leading-relaxed mb-5">
          Whether you've just been bereaved, or you're preparing for your family, there's a gentle path from here.
        </p>
        <div className="flex flex-wrap gap-3">
          <Link
            to="/guide/bereaved"
            data-testid="about-cta-bereaved"
            className="btn-secondary text-sm inline-flex items-center gap-1.5"
          >
            If you've been bereaved <ArrowRight size={14} />
          </Link>
          <Link
            to="/guide/prepare"
            data-testid="about-cta-prepare"
            className="btn-primary text-sm inline-flex items-center gap-1.5"
          >
            Prepare for your family <ArrowRight size={14} />
          </Link>
        </div>
      </div>
    </div>
  );
}
